import { FC, Fragment, createPortal } from "preact/compat";
import { useRef } from "preact/hooks";
import { Transition } from "@headlessui/react";
import { useSignal, useSignalEffect } from "@preact/signals";
import { useApp } from "~/core";
import { usePortalTarget, useTimeout } from "~/hooks";
import { SwipePanelProps } from "./interfaces";
import useSwipePanel from "./useSwipePanel";
import cn from "classnames";

const SwipePanel: FC<SwipePanelProps> = (props) => {
    const target = usePortalTarget();
    const root = useRef<HTMLDivElement>(null);
    const { dialog, mobile } = useApp();
    const shiftX = useSwipePanel(props);
    const isFixed = useSignal(mobile.focus.peek());
    const delayUnfocus = useTimeout(100);

    useSignalEffect(() => {
        const focused = mobile.focus.value;

        if (focused) {
            isFixed.value = true;
            delayUnfocus(false);
        } else {
            delayUnfocus(() => (isFixed.value = false));
        }
    });

    if (!target.value) {
        return null;
    }

    return createPortal(
        <Transition
            as={Fragment}
            show={props.active.value || shiftX.value !== 0}
            leave="transform transition-transform duration-200 ease-out"
            leaveFrom={
                props.to === "right" ? "translate-x-full" : "-translate-x-full"
            }
            leaveTo="translate-x-0"
            beforeEnter={() => {
                const r = root.current;

                if (r) {
                    r.style.transform = "";
                    r.getBoundingClientRect();
                    r.style.transform = `translateX(calc(${
                        props.to === "right" ? "100%" : "-100%"
                    } + ${shiftX.peek()}px))`;
                }

                props.beforeEnter?.();
                dialog.isSwipePanel.value = true;
            }}
            afterEnter={props.afterEnter}
            beforeLeave={() => {
                props.beforeLeave?.();
                dialog.isSwipePanel.value = false;
            }}
            afterLeave={props.afterLeave}
        >
            <div
                ref={root}
                inert={props.inert}
                style={{
                    zIndex: props.zIndex ?? 10,
                    transition: shiftX.value
                        ? undefined
                        : "transform 200ms ease-out",
                    transform: props.active.value
                        ? `translateX(calc(${
                              props.to === "right" ? "100%" : "-100%"
                          } + ${shiftX.value}px))`
                        : `translateX(${shiftX.value}px)`,
                }}
                className={cn(
                    "h-main-content fixed top-safe-header w-screen",
                    props.to === "right" ? "-left-full" : "-right-full",
                    props.containerClassName,
                    props.inert && "pointer-events-none",
                )}
            >
                <div
                    className={cn(
                        "h-main-content max-h-main-content",
                        isFixed.value &&
                            "!h-d-main-content !max-h-d-main-content",
                        "overflow-y-overlay",
                        props.className,
                    )}
                >
                    {props.children}
                </div>
            </div>
        </Transition>,
        target.value,
    );
};

export default SwipePanel;
